import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { getSaintByDate, getSaintToday, likePost } from '../lib/api';
import { PostCard } from './DashboardPage';
import { Star, ChevronLeft, ChevronRight, CalendarDays, Loader2, BookOpen } from 'lucide-react';

export default function SaintOfTheDayPage({ user }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [saintData, setSaintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedPost, setFeedPost] = useState(null);

  const dateKey = useCallback((d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const isToday = dateKey(selectedDate) === dateKey(new Date());

  const loadSaint = useCallback(async (date) => {
    setLoading(true);
    setSaintData(null);
    setFeedPost(null);
    try {
      const dk = dateKey(date);
      const res = await getSaintByDate(dk);
      const data = res.data;
      setSaintData(data.saint);
      if (data.post) setFeedPost(data.post);
      if (data.image_url && data.saint) {
        data.saint._image_url = data.image_url;
      }
    } catch (err) {
      toast.error('Failed to load saint data');
    }
    setLoading(false);
  }, [dateKey]);

  useEffect(() => {
    loadSaint(selectedDate);
  }, [selectedDate, loadSaint]);

  const goDay = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d);
  };

  const saint = saintData;
  const imageUrl = saint?._image_url || feedPost?.image_url;

  const formatDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const colorMap = {
    'Solemnity': 'bg-[#D4AF37] text-white',
    'Feast': 'bg-[#8B4513] text-white',
    'Memorial': 'bg-[#5C4033] text-white',
    'Optional Memorial': 'bg-[#8B7355] text-white',
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="saint-of-day-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#8B4513]/10 flex items-center justify-center">
          <Star className="w-5 h-5 text-[#8B4513]" />
        </div>
        <div>
          <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">Saint of the Day</h1>
          <p className="text-sm text-[#6B665E] font-['Nunito']">Learn about the saints of the Universal Roman Calendar</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Date Navigation */}
          <div className="flex items-center justify-between bg-white border border-[#E5E0D5] rounded-lg px-4 py-3">
            <Button variant="ghost" size="icon" onClick={() => goDay(-1)} data-testid="saint-prev-day">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">{formatDate(selectedDate)}</p>
              {isToday && <Badge className="bg-[#8B4513] text-white text-[10px] font-['Nunito'] mt-1">Today</Badge>}
            </div>
            <Button variant="ghost" size="icon" onClick={() => goDay(1)} data-testid="saint-next-day">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Saint Content */}
          {loading ? (
            <Card className="bg-white border-[#E5E0D5]">
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-48 w-full rounded-lg" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ) : saint ? (
            <Card className="bg-white border-[#E5E0D5] border-l-4 border-l-[#8B4513] overflow-hidden" data-testid="saint-detail-card">
              <CardContent className="p-6 space-y-5">
                {/* Saint Name & Title */}
                <div>
                  <h2 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">{saint.saint_name}</h2>
                  {saint.short_title && (
                    <p className="text-sm text-[#8B4513] font-['Nunito'] italic mt-1">{saint.short_title}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {saint.feast_type && (
                      <Badge className={`text-xs font-['Nunito'] ${colorMap[saint.feast_type] || 'bg-[#8B7355] text-white'}`}>
                        {saint.feast_type}
                      </Badge>
                    )}
                    {saint.birth_year && saint.death_year && (
                      <span className="text-xs text-[#6B665E] font-['Nunito']">{saint.birth_year} - {saint.death_year}</span>
                    )}
                  </div>
                </div>

                {/* Image */}
                {imageUrl && (
                  <div className="flex justify-center">
                    <img
                      src={imageUrl}
                      alt={saint.saint_name}
                      className="max-h-80 rounded-lg border border-[#E5E0D5] object-contain"
                      data-testid="saint-image"
                    />
                  </div>
                )}

                {/* Patron */}
                {saint.patron_of && (
                  <div className="bg-[#F9F7F2] rounded-lg px-4 py-3 border border-[#E5E0D5]">
                    <p className="text-sm font-['Nunito'] text-[#2D2A26]">
                      <span className="font-semibold">Patron of:</span> {saint.patron_of}
                    </p>
                  </div>
                )}

                {/* Biography */}
                {saint.biography && (
                  <div>
                    <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">Biography</h3>
                    <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed">{saint.biography}</p>
                  </div>
                )}

                {/* Reflection */}
                {saint.reflection && (
                  <div className="bg-[#8B4513]/5 rounded-lg px-4 py-3 border-l-2 border-l-[#8B4513]">
                    <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#8B4513] mb-1">Reflection</h3>
                    <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed italic">{saint.reflection}</p>
                  </div>
                )}

                {/* Fun Fact */}
                {saint.fun_fact && (
                  <div className="bg-[#D4AF37]/10 rounded-lg px-4 py-3">
                    <p className="text-sm font-['Nunito'] text-[#2D2A26]">
                      <span className="font-semibold text-[#8B4513]">Did you know?</span> {saint.fun_fact}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-[#E5E0D5]">
              <CardContent className="p-12 text-center">
                <Star className="w-10 h-10 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No saint data available for this date</p>
                <Button variant="outline" className="mt-4 border-[#E5E0D5]" onClick={() => loadSaint(selectedDate)}>
                  <Loader2 className="w-4 h-4 mr-2" /> Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Feed Post (if today's and exists) */}
          {feedPost && isToday && (
            <div>
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#8B4513]" /> Community Post
              </h3>
              <PostCard
                post={feedPost}
                currentUserId={user?.user_id}
                onLike={async (postId) => {
                  try {
                    const res = await likePost(postId);
                    setFeedPost(prev => prev ? ({
                      ...prev,
                      user_liked: res.data.liked,
                      like_count: res.data.like_count
                    }) : prev);
                  } catch {}
                }}
                onComment={() => {}}
              />
            </div>
          )}
        </div>

        {/* Sidebar Calendar */}
        <div className="space-y-4">
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="w-4 h-4 text-[#8B4513]" />
                <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">Select Date</h3>
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                className="rounded-lg border border-[#E5E0D5]"
                data-testid="saint-calendar"
              />
              {!isToday && (
                <Button
                  variant="outline"
                  className="w-full mt-3 border-[#E5E0D5] font-['Nunito'] text-sm"
                  onClick={() => setSelectedDate(new Date())}
                  data-testid="saint-go-today"
                >
                  Go to Today
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
